import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Permission, PurchaseStatus } from '@construction/shared';
import { api } from '@/api/client';
import {
  Alert,
  Badge,
  Button,
  DetailList,
  DetailSection,
  Drawer,
  Field,
  ListGroup,
  ListRow,
  Textarea,
  Timeline,
} from '@/components/ui';
import { FullscreenViewer } from '@/features/drawings/FullscreenViewer';
import { LazyMediaViewer } from '@/features/drawings/LazyViewer';
import { RecordFlag, RecordTime } from '@/features/records/RecordTime';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { formatMoney } from '@/lib/format';
import { usePermissions } from '@/lib/permissions';

/**
 * 采购的列表行和详情（CR-082）。「本工地」和「我的」共用；表格在页面里（它有行内审批和勾选）。
 */

/** 列表和详情要的字段：「本工地」的 Purchase 和「我的」的 MyPurchase 都满足 */
export interface PurchaseLike {
  id: string;
  purchasedAt: string;
  vendor: string | null;
  category: string | null;
  amountCents: number;
  currency: string;
  note: string | null;
  status: PurchaseStatus;
  reviewNote: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  user?: { name: string };
  receipts: Array<{ id: string; url: string; thumbUrl: string | null }>;
}

/**
 * 四种状态四种颜色（CR-033）。改之前「待报销」和「已报销」都是绿色，扫一眼分不出哪几笔还欠着工人的钱。
 * 橙 = 等我批，蓝 = 等我付，绿 = 结清了，红 = 不给。
 */
export const PURCHASE_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'danger' | 'brand'> = {
  PENDING: 'warn',
  APPROVED: 'brand',
  REIMBURSED: 'ok',
  REJECTED: 'danger',
};

/**
 * 服务端允许的状态流转。**界面上只出现这里有的按钮**（CR-031）——
 * 让人点一个注定被 422 拒绝的按钮，是把服务端的规则用报错的方式教给他。
 * 这份表和 `purchases.service.ts` 的 `ALLOWED_TRANSITIONS` 必须一致。第一个是主动作。
 */
export const NEXT_STATUSES: Record<string, PurchaseStatus[]> = {
  PENDING: [PurchaseStatus.APPROVED, PurchaseStatus.REJECTED],
  APPROVED: [PurchaseStatus.REIMBURSED, PurchaseStatus.REJECTED],
  REJECTED: [PurchaseStatus.PENDING],
  REIMBURSED: [],
};

/** 动作按钮的文案。显式映射，不靠拼 key */
export const ACTION_KEY: Record<string, string> = {
  APPROVED: 'purchases.approve',
  REJECTED: 'purchases.reject',
  REIMBURSED: 'purchases.markReimbursed',
  PENDING: 'purchases.reopen',
};

export function PurchaseStatusBadge({ status }: { status: PurchaseStatus }) {
  const { t } = useTranslation();
  return <Badge tone={PURCHASE_TONE[status] ?? 'neutral'}>{t(`purchases.status${status}`)}</Badge>;
}

/** 审批（和 App 待办、批量报销走同一个接口）。成功后让列表、统计、概览、我的都重拉 */
export function useReviewPurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: PurchaseStatus; note?: string }) =>
      api.post(`/purchases/${id}/review`, { status, note: note?.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['my-purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

/** 手机、窄屏的列表：商家 · 类别，人 · 日期；右边金额和状态。能批量的时候左边一个勾 */
export function PurchaseList<T extends PurchaseLike>({
  purchases,
  timezone,
  onOpen,
  selectable = false,
  selected,
  onSelect,
  showUser = true,
  className,
}: {
  purchases: readonly T[];
  timezone: string;
  onOpen: (id: string) => void;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelect?: (id: string, on: boolean) => void;
  showUser?: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  if (purchases.length === 0) return null;
  return (
    <ListGroup className={className}>
      {purchases.map((purchase) => (
        <ListRow
          key={purchase.id}
          onClick={() => onOpen(purchase.id)}
          leading={
            selectable && (
              <input
                type="checkbox"
                checked={selected?.has(purchase.id) ?? false}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSelect?.(purchase.id, e.target.checked)}
                aria-label={t('purchases.selectOne')}
                className="size-4 accent-brand-600"
              />
            )
          }
          title={
            <>
              {purchase.vendor || t('purchases.noVendor')}
              {purchase.category && <span className="font-normal text-ink-3"> · {purchase.category}</span>}
            </>
          }
          subtitle={
            <span className="inline-flex items-center gap-2">
              {[showUser ? purchase.user?.name : null, day(purchase.purchasedAt, i18n.language, timezone)]
                .filter(Boolean)
                .join(' · ')}
              <RecordFlag recordedAt={purchase.createdAt} timeZone={timezone} businessDay={purchase.purchasedAt} />
            </span>
          }
          trailing={
            <>
              <b className="text-body font-semibold tabular-nums text-ink">
                {formatMoney(purchase.amountCents, i18n.language, purchase.currency)}
              </b>
              <PurchaseStatusBadge status={purchase.status} />
            </>
          }
        />
      ))}
    </ListGroup>
  );
}

/**
 * 一笔采购的详情（CR-082）：小票 → 信息 → 进度（提交 → 批准 → 报销）→ 审核意见 → 底部动作。
 * 动作只出服务端允许的那几个（`NEXT_STATUSES`），只有能审批的人看得到（CR-040）。
 * `focusNote`：从表格行上点「驳回」进来的，光标直接在审核意见里 —— 驳回要写原因。
 * `readOnly`：「我的」里只看不批 —— 改之前「我的」就没有审批按钮，自己的采购不在自己手里批。
 */
export function PurchaseDetail({
  purchase,
  timezone,
  onClose,
  focusNote = false,
  readOnly = false,
}: {
  purchase: PurchaseLike | null;
  timezone: string;
  onClose: () => void;
  focusNote?: boolean;
  readOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const canReview = usePermissions().can(Permission.PURCHASE_REVIEW);
  const review = useReviewPurchase();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  // 换了一笔就清掉上一笔的意见和报错
  useEffect(() => {
    setNote('');
    setError(null);
  }, [purchase?.id]);

  const next = purchase && canReview && !readOnly ? (NEXT_STATUSES[purchase.status] ?? []) : [];
  const money = purchase ? formatMoney(purchase.amountCents, i18n.language, purchase.currency) : '';
  const fmt = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString(i18n.language, { timeZone: timezone, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '';

  const act = (status: PurchaseStatus) => {
    if (!purchase) return;
    setError(null);
    review.mutate(
      { id: purchase.id, status, note },
      { onSuccess: onClose, onError: (err) => setError(toMessage(err)) },
    );
  };

  return (
    <>
      <Drawer
        open={purchase !== null}
        onClose={onClose}
        title={purchase ? `${purchase.vendor || t('purchases.noVendor')} · ${money}` : ''}
        subtitle={purchase && <PurchaseStatusBadge status={purchase.status} />}
        footer={
          next.length > 0 &&
          // 右对齐、主动作在最右：NEXT_STATUSES 里第一个是主动作，所以倒着排
          [...next].reverse().map((target, index, ordered) => (
            <Button
              key={target}
              variant={
                index === ordered.length - 1 ? 'primary' : target === PurchaseStatus.REJECTED ? 'dangerOutline' : 'secondary'
              }
              loading={review.isPending && review.variables?.status === target}
              onClick={() => act(target)}
            >
              {t(ACTION_KEY[target] ?? 'app.save')}
            </Button>
          ))
        }
      >
        {purchase && (
          <div className="flex flex-col gap-5">
            <DetailSection title={t('purchases.receipt')}>
              {purchase.receipts.length > 0 ? (
                <ul className="grid grid-cols-3 gap-2">
                  {purchase.receipts.map((receipt) => (
                    <li key={receipt.id}>
                      {/* 小票也走全屏查看器：手写的单据经常要放大才看得清数字 */}
                      <button type="button" onClick={() => setViewing(receipt.url)} className="block w-full">
                        <ReceiptThumb url={receipt.thumbUrl ?? receipt.url} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-body text-safety-600">{t('purchases.noReceipt')}</p>
              )}
            </DetailSection>

            <DetailSection title={t('purchases.summaryTitle')}>
              <DetailList
                items={[
                  [t('purchases.amount'), <b key="a" className="font-semibold tabular-nums">{money}</b>],
                  [
                    t('purchases.date'),
                    <span key="d" className="inline-flex flex-wrap items-center gap-2">
                      {day(purchase.purchasedAt, i18n.language, timezone)}
                      <RecordTime recordedAt={purchase.createdAt} timeZone={timezone} businessDay={purchase.purchasedAt} />
                    </span>,
                  ],
                  purchase.user && [t('logs.submitter'), purchase.user.name],
                  [t('purchases.category'), purchase.category || t('purchases.uncategorized')],
                  [t('purchases.vendor'), purchase.vendor || t('purchases.noVendor')],
                  purchase.note && [t('purchases.note'), <span key="n" className="whitespace-pre-wrap">{purchase.note}</span>],
                ]}
              />
            </DetailSection>

            {/* 「提交 → 批准 → 报销」（CR-031）。驳回的那条单独一站：流程被打断了 */}
            <DetailSection title={t('purchases.stepSubmitted')}>
              <Timeline
                items={
                  purchase.status === PurchaseStatus.REJECTED
                    ? [
                        { key: 'submitted', title: t('purchases.stepSubmitted'), meta: fmt(purchase.createdAt), tone: 'done' },
                        { key: 'rejected', title: t('purchases.statusREJECTED'), meta: [fmt(purchase.reviewedAt), purchase.reviewNote].filter(Boolean).join(' · '), tone: 'bad' },
                      ]
                    : [
                        { key: 'submitted', title: t('purchases.stepSubmitted'), meta: fmt(purchase.createdAt), tone: 'done' },
                        {
                          key: 'approved',
                          title: t('purchases.stepApproved'),
                          meta: purchase.status !== PurchaseStatus.PENDING ? fmt(purchase.reviewedAt) : undefined,
                          tone: purchase.status === PurchaseStatus.PENDING ? 'current' : 'done',
                        },
                        {
                          key: 'reimbursed',
                          title: t('purchases.stepReimbursed'),
                          meta: purchase.status === PurchaseStatus.REIMBURSED ? fmt(purchase.reviewedAt) : undefined,
                          tone:
                            purchase.status === PurchaseStatus.REIMBURSED
                              ? 'done'
                              : purchase.status === PurchaseStatus.APPROVED
                                ? 'current'
                                : 'todo',
                        },
                      ]
                }
              />
              {purchase.reviewNote && purchase.status !== PurchaseStatus.REJECTED && (
                <p className="mt-3 rounded-lg bg-surface-2 px-3 py-2 text-body text-ink-2">{purchase.reviewNote}</p>
              )}
            </DetailSection>

            {next.length > 0 && (
              <Field label={t('purchases.reviewNote')} htmlFor={`pn-${purchase.id}`}>
                <Textarea
                  autoFocus={focusNote}
                  id={`pn-${purchase.id}`}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                />
              </Field>
            )}

            {error && <Alert>{error}</Alert>}
          </div>
        )}
      </Drawer>

      {viewing && purchase && (
        <FullscreenViewer
          title={money}
          meta={[purchase.user?.name, day(purchase.purchasedAt, i18n.language, timezone)].filter(Boolean).join(' · ')}
          onClose={() => setViewing(null)}
        >
          <LazyMediaViewer url={viewing} mimeType="image/jpeg" />
        </FullscreenViewer>
      )}
    </>
  );
}

/**
 * 小票缩略图，**加载失败时说出来**（CR-037）：空白方块什么也没告诉工人，
 * 他只能报「像是链接错误」，我们也无从判断是网络、是地址过期还是被 WebView 拦了。
 */
export function ReceiptThumb({ url, className }: { url: string; className?: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="flex aspect-[3/4] w-full items-center justify-center rounded-lg border border-dashed border-rule px-1 text-center text-micro leading-tight text-ink-3">
        {t('logs.mediaLoadFailed')}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={className ?? 'aspect-[3/4] w-full rounded-lg border border-rule object-cover'}
    />
  );
}

/** 购买日期。表单交的是当地日期的中午（PurchaseForm），按工地时区渲染不会跨天 */
export function day(iso: string, language: string, timezone: string): string {
  return new Date(iso).toLocaleDateString(language, { timeZone: timezone, month: 'numeric', day: 'numeric' });
}
